import { NotificationTheme } from '../../../constants/notification';
import { getProjectScopeByRoute } from '../../../redux/projects/projects.selectors';
import { Dispatch, GetState } from '../../../redux/redux.types';
import { CreateFlowPayload, Flow } from '../../../types/flow.types';
import i18n from '../../../utils/i18n';
import { NewFlowFormData } from '../flow-details-panel/flow/add-new-flow/add-new-flow-form.types';
import * as flowsService from '../flows.service';
import { isFlowActive } from '../flows.utils';

import { activateFlowVersion } from './flow-versions-list.actions';
import {
  buildLoadDataCachedFn,
  fetchDataOnProjectChange,
} from '../../../redux/actions/router/utils/utils';
import { NotificationPlacement } from '../../../components/common/notifications/notifications.types';

export const FLOWS_LIST_ACTION_NAMES = {
  GET_START: 'FLOWS_LIST_ACTION_NAMES__GET_START',
  GET_SUCCESS: 'FLOWS_LIST_ACTION_NAMES__GET_SUCCESS',
  GET_ERROR: 'FLOWS_LIST_ACTION_NAMES__GET_ERROR',

  CREATE_START: 'FLOWS_LIST_ACTION_NAMES__CREATE_START',
  CREATE_SUCCESS: 'FLOWS_LIST_ACTION_NAMES__CREATE_SUCCESS',
  CREATE_ERROR: 'FLOWS_LIST_ACTION_NAMES__CREATE_ERROR',

  RENAME_FLOW_START: 'FLOWS_LIST_ACTION_NAMES__RENAME_FLOW_START',
  RENAME_FLOW_SUCCESS: 'FLOWS_LIST_ACTION_NAMES__RENAME_FLOW_SUCCESS',
  RENAME_FLOW_ERROR: 'FLOWS_LIST_ACTION_NAMES__RENAME_FLOW_ERROR',

  REMOVE_FLOW_START: 'FLOWS_LIST_ACTION_NAMES__REMOVE_FLOW_START',
  REMOVE_FLOW_SUCCESS: 'FLOWS_LIST_ACTION_NAMES__REMOVE_FLOW_SUCCESS',
  REMOVE_FLOW_ERROR: 'FLOWS_LIST_ACTION_NAMES__REMOVE_FLOW_ERROR',

  DUPLICATE_FLOW_START: 'FLOWS_LIST_ACTION_NAMES__DUPLICATE_FLOW_START',
  DUPLICATE_FLOW_SUCCESS: 'FLOWS_LIST_ACTION_NAMES__DUPLICATE_FLOW_SUCCESS',
  DUPLICATE_FLOW_ERROR: 'FLOWS_LIST_ACTION_NAMES__DUPLICATE_FLOW_ERROR',

  DEACTIVATE_FLOW_START: 'FLOWS_LIST_ACTION_NAMES__DEACTIVATE_FLOW_START',
  DEACTIVATE_FLOW_SUCCESS: 'FLOWS_LIST_ACTION_NAMES__DEACTIVATE_FLOW_SUCCESS',
  DEACTIVATE_FLOW_ERROR: 'FLOWS_LIST_ACTION_NAMES__DEACTIVATE_FLOW_ERROR',
};

export const getFlowsList = (context, dispatch, getState) => {
  {
    const projectContext = getProjectScopeByRoute(getState());

    return dispatch({
      types: [
        FLOWS_LIST_ACTION_NAMES.GET_START,
        FLOWS_LIST_ACTION_NAMES.GET_SUCCESS,
        FLOWS_LIST_ACTION_NAMES.GET_ERROR,
      ],
      callApi: () => flowsService.getFlowsList(projectContext),
    });
  }
};

export const getFlowsCached = (context) => (dispatch, getState) => {
  return flowsLoadDataCached(context, dispatch, getState);
};

const flowsLoadDataCached = buildLoadDataCachedFn(
  getFlowsList,
  fetchDataOnProjectChange,
);

export function createFlow(formData: NewFlowFormData) {
  return (dispatch: Dispatch, getState: GetState) => {
    const projectContext = getProjectScopeByRoute(getState());
    const payload: CreateFlowPayload = {
      name: formData.name,
      actions: [JSON.parse(formData.jsonString)],
      active: formData.active,
      on: {
        type: formData.triggerType,
        bodyFilters: formData.bodyFilters,
      },
    };

    return dispatch({
      types: [
        FLOWS_LIST_ACTION_NAMES.CREATE_START,
        {
          name: FLOWS_LIST_ACTION_NAMES.CREATE_SUCCESS,
          notification: {
            message: i18n.t('flows.notifications.add-new-flow.success'), // TODO: get rid of i18n
            theme: NotificationTheme.Success,
            placement: NotificationPlacement.GLOBAL,
          },
        },
        {
          name: FLOWS_LIST_ACTION_NAMES.CREATE_ERROR,
          notification: {
            message: i18n.t('flows.notifications.add-new-flow.error'),
            theme: NotificationTheme.Error,
          },
        },
      ],
      callApi: () => flowsService.createFlow(projectContext, payload),
    });
  };
}

export function renameFlow(flowId: string, newName: string) {
  return (dispatch: Dispatch, getState: GetState) => {
    const projectContext = getProjectScopeByRoute(getState());

    return dispatch({
      types: [
        FLOWS_LIST_ACTION_NAMES.RENAME_FLOW_START,
        FLOWS_LIST_ACTION_NAMES.RENAME_FLOW_SUCCESS,
        FLOWS_LIST_ACTION_NAMES.RENAME_FLOW_ERROR,
      ],
      additionalData: {
        flowId,
        newName,
      },
      callApi: () => flowsService.renameFlow(projectContext, flowId, newName),
    });
  };
}

export function removeFlow(flowId: string) {
  return (dispatch: Dispatch, getState: GetState) => {
    const projectContext = getProjectScopeByRoute(getState());

    return dispatch({
      types: [
        FLOWS_LIST_ACTION_NAMES.REMOVE_FLOW_START,
        {
          name: FLOWS_LIST_ACTION_NAMES.REMOVE_FLOW_SUCCESS,
          notification: {
            message: i18n.t('flows.notifications.remove-flow.success'),
            theme: NotificationTheme.Success,
          },
        },
        {
          name: FLOWS_LIST_ACTION_NAMES.REMOVE_FLOW_ERROR,
          notification: {
            message: i18n.t('flows.notifications.remove-flow.error'),
            theme: NotificationTheme.Error,
          },
        },
      ],
      additionalData: {
        flowId,
      },
      callApi: () => flowsService.removeFlow(projectContext, flowId),
    });
  };
}

export function duplicateFlow(flow: Flow) {
  return (dispatch: Dispatch, getState: GetState) => {
    const projectContext = getProjectScopeByRoute(getState());
    const { watch } =
      flow.activeRevision || flow.lastActiveRevision || flow.latestRevision;

    const payload: CreateFlowPayload = {
      name: flow.name,
      actions: watch.actions,
      active: isFlowActive(flow),
      on: {
        type: watch.on.type,
        bodyFilters: watch.on.bodyFilters,
      },
    };

    return dispatch({
      types: [
        FLOWS_LIST_ACTION_NAMES.DUPLICATE_FLOW_START,
        FLOWS_LIST_ACTION_NAMES.DUPLICATE_FLOW_SUCCESS,
        FLOWS_LIST_ACTION_NAMES.DUPLICATE_FLOW_ERROR,
      ],
      callApi: () => flowsService.createFlow(projectContext, payload),
    });
  };
}

export interface DeactivateFlowActionAdditionalData {
  flowId: string;
}

export function toggleFlowActive(flow: Flow) {
  return (dispatch: Dispatch, getState: GetState) => {
    const projectContext = getProjectScopeByRoute(getState());

    if (isFlowActive(flow)) {
      const additionalData: DeactivateFlowActionAdditionalData = {
        flowId: flow.id,
      };

      return dispatch({
        types: [
          FLOWS_LIST_ACTION_NAMES.DEACTIVATE_FLOW_START,
          FLOWS_LIST_ACTION_NAMES.DEACTIVATE_FLOW_SUCCESS,
          FLOWS_LIST_ACTION_NAMES.DEACTIVATE_FLOW_ERROR,
        ],
        additionalData,
        callApi: () => flowsService.deactivateFlow(projectContext, flow.id),
      });
    }
    activateFlowVersion(flow.lastActiveRevision || flow.latestRevision)(
      dispatch,
      getState,
    );
  };
}
